import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import {
  invoiceRequestApi,
  FG160,
  FG161,
  FG164,
  CreateRequest,
} from '../../../services/invoiceRequestApi';

export type Mode = 'browse' | 'add' | 'edit';

const emptyDetail = (): Partial<FG161> => ({
  fg161_02: 0,
  fg161_17: 0,
  fg161_18: 0,
  fg161_21: '',
  fg161_22: '',
  fg161_26: '',
});

const emptyMaster = (): Partial<FG160> => ({
  fg160_11: new Date().toISOString().slice(0, 10),
  fg160_13: 1,
  fg160_14: 1,
  fg160_15: '',
  fg160_18: '',
  fg160_19: 'NTD',
  fg160_21: 1,
  fg160_24: 'F',
  fg160_17: '',
  fg160_32: '',
});

export function validateInvoiceRequestClient(master: Partial<FG160>, details: Partial<FG161>[]): string[] {
  const errors: string[] = [];
  if (!master.fg160_11) errors.push('fg160_11');
  if (!String(master.fg160_18 || '').trim()) errors.push('fg160_18');
  if (!String(master.fg160_15 || '').trim()) errors.push('fg160_15');
  if (!String(master.fg160_19 || '').trim()) errors.push('fg160_19');
  if (!String(master.fg160_24 || '').trim()) errors.push('fg160_24');
  if (Number(master.fg160_21 || 0) <= 0) errors.push('fg160_21');
  if (details.length < 1) errors.push('details');
  details.forEach((detail, index) => {
    if (Number(detail.fg161_17 || 0) <= 0 && Number(detail.fg161_18 || 0) <= 0) errors.push(`details[${index}].fg161_17`);
    if (['F', 'H', 'I'].includes(String(master.fg160_24 || '')) && !String(detail.fg161_21 || '').trim()) {
      errors.push(`details[${index}].fg161_21`);
    }
    if (!String(detail.fg161_22 || '').trim()) errors.push(`details[${index}].fg161_22`);
    if (master.fg160_24 === 'G' && !String(detail.fg161_26 || '').trim()) errors.push(`details[${index}].fg161_26`);
  });
  return errors;
}

export function useInvoiceRequestForm(canView = true) {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FG160[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form state
  const [master, setMaster] = useState<Partial<FG160>>(emptyMaster());
  const [details, setDetails] = useState<Partial<FG161>[]>([emptyDetail()]);
  const [invoices, setInvoices] = useState<Partial<FG164>[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';
  const isDraft = current?.fg160_80 === 'A' || !current;

  // Load records
  const loadRecords = useCallback(async (keyword?: string) => {
    if (!canView) return [];
    setLoading(true);
    try {
      const res = await invoiceRequestApi.list(1, 200, keyword);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, [canView]);

  // Sync form from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setMaster({ ...current });
      setDetails(current.details || []);
      setInvoices(current.invoices || []);
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Load full record when navigating
  useEffect(() => {
    if (current && mode === 'browse' && !current.details) {
      invoiceRequestApi.get(current.fg160_01).then(full => {
        setRecords(prev => prev.map((r, i) => i === currentIdx ? full : r));
      }).catch(() => {});
    }
  }, [current, currentIdx, mode]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add
  const startAdd = () => {
    setMode('add');
    setMaster(emptyMaster());
    setDetails([emptyDetail()]);
    setInvoices([]);
  };

  // Start edit
  const startEdit = () => {
    if (!current) return;
    if (!isDraft) {
      message.warning('只有草稿狀態可以修改');
      return;
    }
    setMode('edit');
  };

  // Cancel
  const cancel = () => {
    setMode('browse');
    if (current) {
      setMaster({ ...current });
      setDetails(current.details || []);
      setInvoices(current.invoices || []);
    }
  };

  // Update master field
  const updateMaster = (field: keyof FG160, value: any) => {
    setMaster(prev => ({ ...prev, [field]: value }));
  };

  // Detail row management
  const addDetailRow = () => {
    setDetails(prev => [...prev, { ...emptyDetail(), fg161_02: prev.length + 1 }]);
  };

  const removeDetailRow = (idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  };

  const updateDetail = (idx: number, field: keyof FG161, value: any) => {
    setDetails(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  };

  // Save
  const save = async () => {
    const clientErrors = validateInvoiceRequestClient(master, details);
    if (clientErrors.length > 0) {
      message.error(`資料驗證失敗：${clientErrors.join(', ')}`);
      return;
    }
    try {
      const payload: CreateRequest = {
        master: master,
        details: details.map((d, i) => ({ ...d, fg161_02: i + 1 })),
        invoices: invoices.map((inv, i) => ({ ...inv, fg164_02: i + 1 })),
      };

      let result: FG160;
      if (mode === 'add') {
        result = await invoiceRequestApi.create(payload);
        message.success('新增成功');
      } else {
        result = await invoiceRequestApi.update(current!.fg160_01, payload);
        message.success('修改成功');
      }

      const targetNo = result.fg160_01;
      const data = await loadRecords();
      const idx = data.findIndex((r: FG160) => r.fg160_01 === targetNo);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await invoiceRequestApi.delete(current.fg160_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error || '刪除失敗');
    }
  };

  // Submit for approval (A -> E)
  const doSubmit = async () => {
    if (!current) return;
    try {
      await invoiceRequestApi.submit(current.fg160_01);
      message.success('送審成功');
      const data = await loadRecords();
      const idx = data.findIndex((r: FG160) => r.fg160_01 === current.fg160_01);
      setCurrentIdx(idx >= 0 ? idx : 0);
    } catch (err: any) {
      message.error(err?.response?.data?.error || '送審失敗');
    }
  };

  // Approve (E->P or X->Z)
  const doApprove = async () => {
    if (!current) return;
    try {
      await invoiceRequestApi.approve(current.fg160_01);
      message.success('核准成功');
      const data = await loadRecords();
      const idx = data.findIndex((r: FG160) => r.fg160_01 === current.fg160_01);
      setCurrentIdx(idx >= 0 ? idx : 0);
    } catch (err: any) {
      message.error(err?.response?.data?.error || '核准失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    loadRecords(searchText).then(data => {
      if (data.length > 0) setCurrentIdx(0);
      else setCurrentIdx(-1);
    });
  };

  return {
    mode, isEditing, isDraft, loading,
    records, current, currentIdx,
    master, details, invoices,
    updateMaster, setDetails, setInvoices,
    addDetailRow, removeDetailRow, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, doSubmit, doApprove,
    quickSearch, loadRecords,
  };
}
